import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MiniAsset } from '@fullyops/data/assets/asset';
import { ActionTypeLabel } from '@fullyops/data/action-types/entities/action-type-entity';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import { CreatedEquipment } from '@fullyops/features/equipments/containers/create-equipment-panel/create-equipment-panel.component';
import {
  NEW_EQUIPMENT_SIDEBAR_TOKEN,
  NewEquipmentSidebarComponent,
} from '@fullyops/features/equipments/containers/new-equipment-sidebar/new-equipment-sidebar.component';
import { newAssetCompanyId } from './new-asset-company';

export type InlineAssetContext = {
  interventionType: ActionTypeLabel;
  requiresUnassignedAsset: boolean;
  companyId: string | null;
};

/**
 * `stacked` is for a caller that is itself a sidebar panel: the creation panel opens on top of it and hands
 * back to it when done, rather than replacing it.
 */
export type InlineAssetPresentation = {
  sidebar: SidenavPortalBridgeService;
  stacked: boolean;
};

export function createInlineAsset(
  presentation: InlineAssetPresentation,
  context: InlineAssetContext,
): Observable<MiniAsset> {
  const companyId = newAssetCompanyId(
    context.interventionType,
    context.requiresUnassignedAsset,
    context.companyId,
  );

  const created = openSidebar(
    presentation,
    companyId,
    context.interventionType,
  );

  return created.pipe(
    // Read the company off the result rather than off the request: the panel
    // creates the equipment unassigned when this user may not set an owner, and
    // a wrong tag would mislabel the option and mis-sort it.
    map(
      (equipment): MiniAsset => ({
        ...equipment,
        ownership: equipment.company == null ? 'UNASSIGNED' : 'CLIENT_OWNED',
      }),
    ),
  );
}

function openSidebar(
  { sidebar, stacked }: InlineAssetPresentation,
  companyId: string | null,
  actionType: ActionTypeLabel,
): Observable<CreatedEquipment> {
  return new Observable<CreatedEquipment>((subscriber) => {
    const close = () => (stacked ? sidebar.popPortal() : sidebar.closePortal());
    const inputs = {
      companyId,
      actionType,
      nested: stacked,
      onSave: (equipment: CreatedEquipment) => {
        close();
        subscriber.next(equipment);
        subscriber.complete();
      },
      onCancel: () => {
        close();
        subscriber.complete();
      },
    };
    if (stacked) {
      sidebar.pushPortal(
        NewEquipmentSidebarComponent,
        NEW_EQUIPMENT_SIDEBAR_TOKEN,
        inputs,
        { width: 'large' },
      );
    } else {
      sidebar.openPortal(
        NewEquipmentSidebarComponent,
        NEW_EQUIPMENT_SIDEBAR_TOKEN,
        inputs,
        { width: 'large' },
      );
    }
  });
}
